'use client';

import { c as _c } from "react/compiler-runtime";
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Modal, type ModalProps } from '@repo/design-system/components/ui/modal';
import { Type } from '@repo/design-system/components/ui/type';
import { overlayBorderStyles } from '@repo/design-system/custom-styles';
import type { MaterialSymbolName } from '@repo/design-system/types/material-symbol-names';
import { useT } from '@repo/i18n/components/i18n.client';
import type { Wandi18nResourcePath } from '@repo/i18n/i18n-resources/resources';
import { selectPath } from '@repo/i18n/select-path';
import { css, cx } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectSteamId, selectTitleName } from '@repo/store/maps/slices/mapData';
import { getSteamImageUrl, getWandSteamCdnImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { Trans } from 'react-i18next';
interface WandExperienceModalProps extends ModalProps {
  cta: React.ReactNode;
}
const features: Array<{
  icon: MaterialSymbolName;
  translationKey: Wandi18nResourcePath<'game_map'>;
}> = [{
  icon: 'near_me',
  translationKey: 'game_map_embed_limit_prompt.live_location_tracking'
}, {
  icon: 'layers',
  translationKey: 'game_map_embed_limit_prompt.wand_native_overlay'
}, {
  icon: 'sprint',
  translationKey: 'game_map_embed_limit_prompt.one_click_teleport'
}, {
  icon: 'push_pin',
  translationKey: 'game_map_embed_limit_prompt.save_pins_more'
}];
export const WandExperienceModal: React.FC<WandExperienceModalProps> = t0 => {
  const $ = _c(34);
  let cta;
  let props;
  if ($[0] !== t0) {
    ({
      cta,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = cta;
    $[2] = props;
  } else {
    cta = $[1];
    props = $[2];
  }
  const gameTitle = useAppSelector(selectTitleName);
  const steamId = useAppSelector(selectSteamId);
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useT(t1);
  let t2;
  if ($[4] !== steamId) {
    t2 = steamId ? getSteamImageUrl(steamId, "hero") : null;
    $[4] = steamId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const heroImageUrl = t2;
  let t3;
  if ($[6] !== steamId) {
    t3 = steamId ? getWandSteamCdnImageUrl(steamId, "capsule") : null;
    $[6] = steamId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const capsuleImageUrl = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx(overlayBorderStyles(), css({
      background: "wemod.elevated.100",
      overflow: "hidden",
      _after: {
        zIndex: -1
      }
    }));
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== heroImageUrl) {
    t5 = heroImageUrl && <><img aria-hidden="true" alt="" src={heroImageUrl} className={css({
        width: "full",
        height: "[120px]",
        position: "absolute",
        zIndex: -1,
        top: "0",
        left: "0",
        objectFit: "cover"
      })} /><div className={css({
        position: "absolute",
        top: "0",
        left: "0",
        right: "0",
        height: "[120px]",
        background: "[linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, var(--colors-wemod-elevated-40) 50%, var(--colors-wemod-elevated-100) 100%)]",
        pointerEvents: "none",
        zIndex: 0
      })} aria-hidden={true} /></>;
    $[9] = heroImageUrl;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css({
      position: "relative",
      zIndex: 1,
      isolation: "isolate"
    });
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== heroImageUrl) {
    t7 = css.raw({
      padding: "16",
      ...(heroImageUrl ? {
        marginTop: "60"
      } : {})
    });
    $[12] = heroImageUrl;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== capsuleImageUrl) {
    t8 = capsuleImageUrl && <div className={cx(overlayBorderStyles(), css({
      width: "[40px]",
      height: "[48px]",
      flexShrink: "0",
      borderRadius: "6"
    }))}><img aria-hidden="true" alt="" src={capsuleImageUrl} className={css({
        width: "full",
        height: "full",
        borderRadius: "6",
        objectFit: "cover"
      })} /></div>;
    $[14] = capsuleImageUrl;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== gameTitle) {
    t9 = <Type variant="header-l" color="white"><Trans i18nKey={_temp} values={{
        gameName: gameTitle,
        count: 100
      }} ns="game_map" /></Type>;
    $[16] = gameTitle;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <FlexColumn align="start" gap="12">{t8}{t9}</FlexColumn>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = features.map(feature => <Type key={feature.icon} variant="body-m" color="white" className={css({
      display: "flex",
      alignItems: "center",
      gap: "12",
      ml: "24"
    })}><Icon icon={feature.icon} size="lg" className={css({
        color: "white",
        flexShrink: "0"
      })} decorative={true} />{t($_0 => selectPath($_0, feature.translationKey))}</Type>);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <FlexColumn gap="12">{t11}</FlexColumn>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== cta || $[26] !== t10 || $[27] !== t12 || $[28] !== t7) {
    t13 = <div className={t6}><FlexColumn gap="16" className={t7}>{t10}{t12}{cta}</FlexColumn></div>;
    $[25] = cta;
    $[26] = t10;
    $[27] = t12;
    $[28] = t7;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== props || $[31] !== t13 || $[32] !== t5) {
    t14 = <Modal {...props} variant="transparent" size="sm" className={t4}>{t5}{t13}</Modal>;
    $[30] = props;
    $[31] = t13;
    $[32] = t5;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
function _temp($) {
  return $.game_map_embed_limit_prompt.get_the_full_experience_wand;
}